import { useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useGet } from 'hooks/useGet';
import CollectionsAPI from 'services/API/CollectionsAPI';
import { CollectionSharedList, CollectionProjectList } from 'types';

type SharedCollectionsResponse = {
  data: CollectionSharedList[];
  message: string;
};

type NewCollection = {
  id: number;
  name: string;
};

export const useCollectionsShared = () => {
  return useGet<SharedCollectionsResponse>('/collections/shared/', []);
};

export const useUpdateCollection = async (id: number, name: string) => {
  CollectionsAPI.updateCollection(id, name);
};

export const removeCollection = async (id: number) => CollectionsAPI.deleteCollection(id);

export const useCollections = () => {
  const queryClient = useQueryClient();
  const getCollectionsList = useQuery(
    ['all-collections'],
    async (): Promise<CollectionProjectList[]> => {
      const result = await CollectionsAPI.getCollections();

      return result?.data?.data || [];
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const invalidateGetCollectionsList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['all-collections'] });
  }, [getCollectionsList]);

  const createNewCollection = useMutation({
    mutationFn: async ({ name }: { name: string }): Promise<NewCollection | null> => {
      const result = await CollectionsAPI.createCollection(name);

      return result?.data as NewCollection | null;
    },
  });

  return { getCollectionsList, invalidateGetCollectionsList, createNewCollection };
};
